<script setup lang="ts">
import { ref } from 'vue'
import Typography from '@/components/ui/typography/Typography.vue'
import QuickStart from '@/components/QuickStart.vue'
import TableConfiguration from '@/components/TableConfiguration.vue'
import Badge from '@/components/ui/badge/Badge.vue'
import Button from '@/components/ui/button/Button.vue'
import EditSvg from '@/assets/icons/edit.svg'
import ArrowLeftSvg from '@/assets/icons/arrow-left.svg'

import { PokerTablePreview2 } from '@acepoker/games-nlhe/frontend'
import {
  generateDrillPreviewSettings,
  generateCustomDrillPreviewSettings
} from '@acepoker/games-nlhe/frontend/helpers/generate-drill-preview-settings'

import { generateSequence, generateSequenceForPredefined } from '@/api/drill/index'

import type { Drill, DrillSequence } from '@acepoker/contract'

const props = defineProps<{
  drill: Drill
  sequence?: DrillSequence[]
  editable?: boolean
  isPredefined?: boolean
}>()

const emit = defineEmits<{
  back: []
  startDrill: [id: Drill['id']]
  editDrill: [id: Drill['id']]
}>()

const tableSettings = ref({
  disabled: true
})

const generatePreview = async () => {
  if (!props.drill) return

  if (props.drill.kind === 'sequence') {
    tableSettings.value = generateCustomDrillPreviewSettings(
      props.drill,
      props.sequence[0].sequence
    )
  } else {
    const moves = props.isPredefined
      ? await generateSequenceForPredefined(props.drill.data)
      : await generateSequence(props.drill.data)
    tableSettings.value = generateDrillPreviewSettings(props.drill, moves)
  }
}
generatePreview()
</script>

<template>
  <section
    :class="`details-content bg-primary2 flex w-full flex-col items-center justify-between overflow-x-hidden overflow-y-auto p-6 pb-0! text-white max-lg:fixed max-lg:top-0 max-lg:right-0 max-lg:bottom-0 max-lg:left-0 max-lg:z-50 lg:rounded-lg 2xl:p-8`"
  >
    <header class="flex w-full items-center lg:hidden">
      <div class="py-4 pr-4 pl-2" @click="emit('back')">
        <arrow-left-svg />
      </div>
      <Typography class="text-custom-22 leading-custom-27 truncate text-left font-semibold">{{
        drill.name || $t('NLHDrillsPage.label.PokerBasics')
      }}</Typography>
    </header>

    <!-- TODO: review needs of separated components -->
    <!-- <DrillDetailsCustom v-if="drill.kind === DrillKind.Sequence" v-bind="props" /> -->
    <div class="mb-[20px] max-h-[calc(100%-140px)] min-h-[100px] shrink-0">
      <PokerTablePreview2 v-bind="tableSettings" />
    </div>

    <div class="grow lg:hidden"></div>

    <footer class="sticky bottom-0 flex w-full flex-col py-4">
      <QuickStart
        :on-click="() => $emit('startDrill', drill.id)"
        customClass="testqiuic mb-0!"
        buttonClass="mt-[16px]!"
      >
        <template #configurationTitle v-if="drill.gaming_configuration">
          <TableConfiguration
            :config="drill.gaming_configuration"
            :maxPlayers="drill.max_players"
            :startingStack="drill.starting_stack"
          />
        </template>
        {{ $t('CustomDrills.button.StartPractice') }}
      </QuickStart>
      <Button
        v-if="props.editable"
        variant="ghostOutlined"
        class="mt-0 mb-2 flex h-[40px] w-full items-center justify-center self-center text-sm lg:max-w-[320px]"
        @click="$emit('editDrill', drill.id)"
      >
        {{ $t('CustomDrills.button.EditDrill') }}
      </Button>
    </footer>
  </section>
</template>
